import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, BookOpen, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Mortgage Basics',
    author: '',
    read_time: '',
    featured: false,
    published: true
  });

  const categories = [
    'Loan Programs',
    'VA Loans', 
    'Mortgage Basics',
    'Market Trends',
    'First-Time Buyers',
    'Refinancing',
    'Alternative Programs'
  ];

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/blog`, { headers });
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/blog`, formData, { headers });
      setBlogPosts([...blogPosts, response.data]);
      setIsCreating(false);
      resetForm();
    } catch (error) {
      console.error('Error creating blog post:', error);
      alert('Error creating blog post');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.put(`${API}/admin/blog/${id}`, formData, { headers });
      setBlogPosts(blogPosts.map(post => 
        post.id === id ? response.data : post
      ));
      setEditingPost(null);
      resetForm();
    } catch (error) {
      console.error('Error updating blog post:', error);
      alert('Error updating blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/blog/${id}`, { headers });
      setBlogPosts(blogPosts.filter(post => post.id !== id));
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Error deleting blog post');
    }
  };

  const startEdit = (post) => {
    setEditingPost(post.id);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      read_time: post.read_time,
      featured: post.featured,
      published: post.published
    });
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setIsCreating(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Mortgage Basics',
      author: '',
      read_time: '',
      featured: false,
      published: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading blog posts...</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/admin" className="flex items-center space-x-2">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Dashboard</span>
                </Link>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Blog Management</h1>
            </div>
            <Button
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Article
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create Form */}
        {isCreating && (
          <Card className="mb-8 border-none bg-white">
            <CardHeader>
              <CardTitle>Add New Article</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Article Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. The Complete Guide to FHA Loans in 2025"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Enter author name"
                  />
                </div>
                <div>
                  <Label htmlFor="read_time">Read Time</Label>
                  <Input
                    id="read_time"
                    name="read_time"
                    value={formData.read_time}
                    onChange={handleInputChange}
                    placeholder="8 min read"
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="featured"
                      checked={formData.featured}
                      onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                    />
                    <Label htmlFor="featured">Featured Article</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="published"
                      checked={formData.published}
                      onCheckedChange={(checked) => setFormData({...formData, published: checked})}
                    />
                    <Label htmlFor="published">Published</Label>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="excerpt">Excerpt</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Brief description that appears on the education page..."
                    rows={3}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="content">Article Content</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Full article content..."
                    rows={8}
                  />
                </div>
              </div>
              <div className="flex space-x-4 mt-6">
                <Button
                  className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                  onClick={handleCreate}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Article
                </Button>
                <Button variant="outline" onClick={cancelEdit}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Blog Posts List */}
        <div className="grid gap-6">
          {blogPosts.map((post) => (
            <Card key={post.id} className="border-none bg-white">
              <CardContent className="p-6">
                {editingPost === post.id ? (
                  // Edit Form
                  <div>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="md:col-span-2">
                        <Label htmlFor={`title-${post.id}`}>Article Title</Label>
                        <Input
                          id={`title-${post.id}`}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`category-${post.id}`}>Category</Label>
                        <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(category => (
                              <SelectItem key={category} value={category}>{category}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`author-${post.id}`}>Author</Label>
                        <Input
                          id={`author-${post.id}`}
                          name="author"
                          value={formData.author}
                          onChange={handleInputChange}
                          placeholder="Enter author name"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`read_time-${post.id}`}>Read Time</Label>
                        <Input
                          id={`read_time-${post.id}`}
                          name="read_time"
                          value={formData.read_time}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`featured-${post.id}`}
                            checked={formData.featured}
                            onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                          />
                          <Label htmlFor={`featured-${post.id}`}>Featured</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`published-${post.id}`}
                            checked={formData.published}
                            onCheckedChange={(checked) => setFormData({...formData, published: checked})}
                          />
                          <Label htmlFor={`published-${post.id}`}>Published</Label>
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`excerpt-${post.id}`}>Excerpt</Label>
                        <Textarea
                          id={`excerpt-${post.id}`}
                          name="excerpt"
                          value={formData.excerpt}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`content-${post.id}`}>Content</Label>
                        <Textarea
                          id={`content-${post.id}`}
                          name="content"
                          value={formData.content}
                          onChange={handleInputChange}
                          rows={8}
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <Button
                        className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                        onClick={() => handleUpdate(post.id)}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={cancelEdit}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 rounded-lg bg-[#0b2c4a]/10 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-8 w-8 text-[#0b2c4a]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{post.title}</h3>
                            {post.featured && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                            <span className="bg-[#0b2c4a]/10 text-[#0b2c4a] px-2 py-1 rounded-md text-xs font-medium">
                              {post.category}
                            </span>
                            <span>By {post.author}</span>
                            <span>{post.read_time}</span>
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                              post.published 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {post.published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(post)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {post.content.substring(0, 200)}...
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {blogPosts.length === 0 && (
          <Card className="border-none bg-white">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Blog Posts</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first article.</p>
              <Button
                className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                onClick={() => setIsCreating(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Article
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;